import type { FireRuleResult } from '@/types/paymentSchedule';

/**
 * Machine-readable note the engine attaches when an auto-charge stage needed
 * no charge because the held deposit already covered it. `notes` is the
 * engine's established channel for these tokens (`auto_charge_paused_consent_*`,
 * `request_payment_only_skipped`), and one fire can carry several joined
 * together, so match on containment rather than equality.
 */
const SETTLED_WITHOUT_CHARGE = 'auto_charge_settled_without_charge';

export interface FireRuleOutcome {
  tone: 'success' | 'warning' | 'error';
  title: string;
  description?: string;
  /** Present when the fire minted a pay link the operator may need to relay. */
  paymentLinkUrl?: string;
}

/**
 * Turn a fire result into what the operator is actually told.
 *
 * "Run now" used to toast "Rule fired" for every 200 — including the case that
 * prompted the QA report, where the rule produced a DRAFT invoice the customer
 * can neither see nor pay, and the case where it minted a payment link that was
 * never sent anywhere. A fire that billed nobody must not read as a success.
 */
export function describeFireOutcome(result: FireRuleResult): FireRuleOutcome {
  if (result.status === 'noop') {
    return {
      tone: 'warning',
      title: 'Rule marked complete without billing',
      description: result.notes ?? 'The rule resolved to nothing to bill.',
    };
  }

  const paymentLinkUrl = result.paymentLinkUrl;

  // The invoice never left DRAFT — the exact failure the customer feels.
  if (result.invoiceStatus === 'DRAFT') {
    return {
      tone: 'error',
      title: 'Invoice created but not issued',
      description: result.notes ?? 'Open the invoice and send it to bill the customer.',
      paymentLinkUrl,
    };
  }

  if (result.paymentId) {
    return {
      tone: 'success',
      title: 'Payment charged',
      description: result.notes,
      paymentLinkUrl,
    };
  }

  // An auto-charge stage already covered by funds in hand. Its invoice still
  // has to be raised and issued — that is what applies the held deposit to it
  // — but issuing is deliberately a no-send there, and no card is touched, so
  // neither "Payment charged" nor "Invoice sent to the customer" is true.
  if (result.notes?.includes(SETTLED_WITHOUT_CHARGE)) {
    return {
      tone: 'success',
      title: 'Settled from funds already collected',
      description:
        'The deposit already held covered this stage in full, so its invoice was issued and closed without charging the card.',
      paymentLinkUrl,
    };
  }

  const delivery = result.delivery;
  // A suppressed contact (do-not-contact / opt-out) is a deliberate no-send
  // rather than a failure — but nothing reached the customer either way, so
  // neither may read as "sent". `combineDeliveryLegs` reports a fully
  // suppressed send as `delivered: true, suppressed: true`, so testing
  // `delivered` alone would let exactly that case through as a success.
  if (delivery && (!delivery.delivered || delivery.suppressed)) {
    const forInvoice = Boolean(result.invoiceId);
    if (delivery.suppressed) {
      return {
        tone: 'warning',
        title: forInvoice
          ? 'Invoice issued, but delivery was suppressed'
          : 'Payment request created, but delivery was suppressed',
        description: 'The contact is opted out of this channel — reach them another way.',
        paymentLinkUrl,
      };
    }
    return {
      tone: 'warning',
      title: forInvoice
        ? 'Invoice issued, but not delivered'
        : 'Payment request created, but not sent',
      description: delivery.error ?? result.notes ?? 'Send it to the customer from the record.',
      paymentLinkUrl,
    };
  }

  if (result.invoiceId) {
    return {
      tone: 'success',
      title: 'Invoice sent to the customer',
      description: result.notes,
      paymentLinkUrl,
    };
  }

  if (paymentLinkUrl) {
    return {
      tone: 'success',
      title: 'Payment link sent to the customer',
      description: result.notes,
      paymentLinkUrl,
    };
  }

  return {
    tone: 'warning',
    title: 'Rule fired, but nothing was billed',
    description: result.notes ?? 'No invoice or payment request was created.',
  };
}
